import { useState } from "react";
import PlatformFooter from "@/components/PlatformFooter";
import { Link, useNavigate } from "react-router-dom";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import PlatformLogo from "@/components/PlatformLogo";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";

const F0 = {
  name: "", cognome: "", email: "", phone: "", password: "", confirm_password: "",
  company_name: "", ragione_sociale: "", piva: "", codice_fiscale: "", indirizzo: "",
  cap: "", comune: "", provincia: "", company_phone: "", company_email: "", sito: "",
  terms_accepted: false, privacy_accepted: false,
};

export default function Register() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [f, setF] = useState(F0);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.terms_accepted) return toast.error(t("register.err_terms"));
    if (!f.privacy_accepted) return toast.error(t("register.err_privacy"));
    if (f.password !== f.confirm_password) return toast.error(t("register.err_pw"));
    setBusy(true);
    try {
      const { data } = await http.post("/public/register", f);
      toast.success(data?.message || "Registrazione completata! Accedi per iniziare la prova gratuita di 7 giorni.", { duration: 8000 });
      nav("/login");
      return data;
    } catch (err) {
      toast.error(apiErr(err, t("register.err_generic")));
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, type = "text", required = false, testid = null) => (
    <div>
      <Label htmlFor={`reg-${k}`} className="text-xs text-slate-600">{label}{required && " *"}</Label>
      <Input id={`reg-${k}`} data-testid={testid || `reg-${k}`} type={type} value={f[k]} onChange={set(k)} required={required} className="mt-1" autoComplete={k.includes("password") ? "new-password" : undefined} />
    </div>
  );
  const legal = (to, label, testid) => (
    <Link to={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className="font-semibold text-sky-700 underline underline-offset-2 hover:text-sky-800">{label}</Link>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="register-page">
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <PlatformLogo size={32} dark={false} testId="register-platform-logo" />
        <div className="flex items-center gap-2">
          <LanguageSelector variant="light" />
          <Button variant="ghost" data-testid="reg-home-btn" onClick={() => nav("/welcome")}><ArrowLeft className="w-4 h-4 mr-1" /> {t("register.home")}</Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("register.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("register.sub")}</p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="font-semibold text-[#0F172A]">{t("register.referent")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("name", t("register.name"), "text", true)}
              {field("cognome", t("register.surname"), "text", true)}
              {field("email", t("register.email"), "email", true, "reg-email")}
              {field("phone", t("register.phone"), "tel")}
              {field("password", t("register.password"), "password", true, "reg-password")}
              {field("confirm_password", t("register.confirm"), "password", true, "reg-confirm")}
            </div>
            <p className="text-[11px] text-slate-400">{t("register.pw_hint")}</p>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="font-semibold text-[#0F172A]">{t("register.company")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("company_name", t("register.company_name"), "text", true, "reg-company")}
              {field("ragione_sociale", t("register.legal_name"))}
              {field("piva", t("register.vat"), "text", true, "reg-piva")}
              {field("codice_fiscale", t("register.tax_code"))}
              {field("indirizzo", t("register.address"))}
              {field("cap", t("register.zip"))}
              {field("comune", t("register.city"))}
              {field("provincia", t("register.province"))}
              {field("company_phone", t("register.company_phone"), "tel")}
              {field("company_email", t("register.company_email"), "email")}
              {field("sito", t("register.website"))}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="reg-terms" checked={f.terms_accepted} onCheckedChange={(v) => setF({ ...f, terms_accepted: !!v })} className="mt-0.5" />
              <span className="text-sm text-slate-700">{t("register.accept_terms_pre")} {legal("/terms", t("register.terms_link"), "reg-terms-link")} {t("register.accept_terms_post")}</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="reg-privacy" checked={f.privacy_accepted} onCheckedChange={(v) => setF({ ...f, privacy_accepted: !!v })} className="mt-0.5" />
              <span className="text-sm text-slate-700">{t("register.accept_privacy_pre")}{legal("/privacy", t("register.privacy_link"), "reg-privacy-link")}</span>
            </label>
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <Button data-testid="reg-submit" type="submit" disabled={busy} className="bg-[#0F172A] hover:bg-[#1e293b]">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {t("register.submit")}
            </Button>
            <button type="button" data-testid="reg-login-link" className="text-sm text-slate-500 underline" onClick={() => nav("/login")}>{t("register.have_account")}</button>
          </div>
        </form>
      </div>
      <PlatformFooter />
    </div>
  );
}
